import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { formatDate as formatDateOnly, toDisplayDate } from '@/lib/utils'
import { CATEGORICAL } from '@/lib/theme'
import {
  ArrowLeft,
  Headphones,
  Building2,
  User,
  Phone,
  Mail,
  Globe,
  Clock,
  MessageSquare,
  Send,
  PhoneIncoming,
  PhoneOutgoing,
  Link2,
  Unlink,
  Inbox,
  Timer,
  Pencil,
  MoreHorizontal,
  Trash2,
  Check,
  X,
  Plus,
  CheckCircle2,
  Circle,
  Calendar,
  Paperclip,
} from 'lucide-react'
import { ClickToCallButton } from '@/components/ClickToCallButton'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { SLAMetrics } from '@/components/ui/sla-indicator'
import { AttachmentsTab } from '@/components/AttachmentsTab'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  useTicket,
  useUpdateTicket,
  useAddTicketComment,
  useUpdateTicketComment,
  useDeleteTicketComment,
  useTicketEmails,
  useTicketTasks,
  useCreateTicketTask,
  useEmails,
  useSendTicketEmail,
  useLinkEmailToTicket,
  useUnlinkEmailFromTicket,
  useAccounts,
  useContacts,
  useProducts,
  useUsers,
} from '@/hooks/use-api'
import { useUser } from '@/contexts/UserContext'
import { useToast } from '@/hooks/use-toast'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useRecordBreadcrumb } from '@/contexts/BreadcrumbContext'
import { useReadOnly } from '@/hooks/use-read-only'
import { useDebounce } from '@/hooks/use-debounce'
import { useClickToEdit } from '@/hooks/use-click-to-edit'
import type {
  TicketStatus,
  TicketPriority,
  TicketChannel,
  TicketComment,
  CommentType,
  TicketCallDisposition,
  Email,
} from '@/types'
import {
  ticketStatusVariants as statusVariants,
  ticketPriorityVariants as priorityVariants,
} from '@/lib/ticket-variants'
import { explainError } from '@/lib/explain-error'

const commentTypeLabels: Record<CommentType, string> = {
  reply: 'Reply',
  note: 'Internal Note',
  call_log: 'Call Log',
  email: 'Email',
}

const callDispositionLabels: Record<TicketCallDisposition, string> = {
  answered: 'Answered',
  voicemail: 'Voicemail',
  missed: 'Missed',
  busy: 'Busy',
  no_answer: 'No Answer',
}

const formatDuration = (seconds?: number): string => {
  if (!seconds || seconds <= 0) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const impersonationReadOnly = useReadOnly()

  const { currentUser, isAdmin, canManageTickets } = useUser()
  // Ticket view is read-only when impersonating OR when the current role cannot
  // manage tickets (Sales/User, Marketing, RFP). This gates every mutation
  // control below (status, priority, edit, delete, tasks, emails) EXCEPT
  // commenting, which creators/owners may also do (see canComment below).
  const isReadOnly = impersonationReadOnly || !canManageTickets
  const { data: ticket, isLoading, error } = useTicket(id!)
  // A failed background refetch must never blank out an already-rendered
  // ticket — keep the content on screen and explain the failure in a toast.
  useEffect(() => {
    if (error && ticket) {
      toast({
        title: 'Ticket refresh failed',
        description: explainError(error, 'Showing the last loaded version'),
        variant: 'destructive',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])
  // Creators/owners may add comments on their own tickets even without
  // ticket-management rights — the backend enforces the same rule. Editing/
  // deleting stays per-comment (author or admin). Impersonation is read-only.
  const canComment =
    !impersonationReadOnly &&
    (canManageTickets ||
      (!!currentUser?.id &&
        (ticket?.created_by_id === currentUser.id || ticket?.owner_id === currentUser.id)))
  useDocumentTitle(
    ticket
      ? `${ticket.subject || `Ticket ${ticket.ticket_number ?? ''}`.trim()} | Ticket`
      : undefined
  )
  useRecordBreadcrumb(
    ticket ? ticket.subject || `Ticket ${ticket.ticket_number ?? ''}`.trim() : undefined
  )
  const updateMutation = useUpdateTicket()
  const addCommentMutation = useAddTicketComment()
  const updateCommentMutation = useUpdateTicketComment()
  const deleteCommentMutation = useDeleteTicketComment()

  // Comment edit/delete state
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [editingCommentBody, setEditingCommentBody] = useState('')
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null)

  // Task hooks
  const { data: ticketTasks = [] } = useTicketTasks(id!)
  const createTicketTaskMutation = useCreateTicketTask()
  const [showTaskForm, setShowTaskForm] = useState(false)
  const [taskForm, setTaskForm] = useState({
    subject: '',
    activity_date: '',
    priority: 'Normal',
    description: '',
  })

  // Email hooks. The all-emails list only feeds the Link Email dialog's picker —
  // fetching the user's whole mailbox (limit 10000, a heavy query) on every
  // ticket view was the likeliest trigger of the "ticket errors after ~3s" bug.
  const [showLinkModal, setShowLinkModal] = useState(false)
  const { data: ticketEmails = [], refetch: refetchEmails } = useTicketEmails(id!)
  const { data: allEmailsData } = useEmails({ limit: 10000 }, { enabled: showLinkModal })
  const sendEmailMutation = useSendTicketEmail()
  const linkEmailMutation = useLinkEmailToTicket()
  const unlinkEmailMutation = useUnlinkEmailFromTicket()

  const [newComment, setNewComment] = useState('')
  const [commentType, setCommentType] = useState<CommentType>('note')
  const [isPublic, setIsPublic] = useState(false)

  // Call log fields
  const [callDirection, setCallDirection] = useState<'inbound' | 'outbound'>('outbound')
  const [callDurationMinutes, setCallDurationMinutes] = useState('')
  const [callDurationSeconds, setCallDurationSeconds] = useState('')
  const [callPhoneNumber, setCallPhoneNumber] = useState('')
  const [callDisposition, setTicketCallDisposition] = useState<TicketCallDisposition>('answered')

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false)
  const [editForm, setEditForm] = useState<Record<string, string>>({})
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  // Edit mode hooks with server-side search
  const [accountSearch, setAccountSearch] = useState('')
  const [contactSearch, setContactSearch] = useState('')
  const [productSearch, setProductSearch] = useState('')
  const debouncedAccountSearch = useDebounce(accountSearch, 300)
  const debouncedContactSearch = useDebounce(contactSearch, 300)
  const debouncedProductSearch = useDebounce(productSearch, 300)

  // These four datasets only feed the edit-mode comboboxes — don't fetch them
  // (5 requests, limit 10000 each) just to view a ticket.
  const { data: accountsData, isFetching: accountsLoading } = useAccounts(
    {
      search: debouncedAccountSearch || undefined,
      limit: 10000,
      sort_by: 'name',
      sort_order: 'asc',
    },
    { enabled: isEditMode }
  )
  const { data: contactsData, isFetching: contactsLoading } = useContacts(
    {
      search: debouncedContactSearch || undefined,
      limit: 10000,
      sort_by: 'last_name',
      sort_order: 'asc',
      account_id: editForm.account_id || undefined,
    },
    { enabled: isEditMode }
  )
  const { data: productsData, isFetching: productsLoading } = useProducts(
    {
      search: debouncedProductSearch || undefined,
      limit: 10000,
      sort_by: 'name',
      sort_order: 'asc',
    },
    { enabled: isEditMode }
  )
  // Active users only for the owner dropdown (edit mode only)
  const { data: usersData } = useUsers({ filter: 'active', limit: 10000 }, { enabled: isEditMode })
  const accounts = accountsData?.data || []
  const allContacts = contactsData?.data || []
  const products = productsData?.data || []
  const users = usersData?.data || []

  // Email modals
  const [showComposeModal, setShowComposeModal] = useState(false)
  const [emailTo, setEmailTo] = useState('')
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  const [emailSearch, setEmailSearch] = useState('')

  // Click-to-edit — must be called before any conditional returns. The
  // lambda closes over `handleStartEdit` which is defined further down;
  // lexical scope means it reads the live binding when the user clicks.
  const { bindReadOnly } = useClickToEdit(isEditMode, () => handleStartEdit())

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96" role="status" aria-live="polite">
        <div className="text-center">
          <div
            className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="progressbar"
            aria-label="Loading ticket details"
          ></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading ticket details...</p>
        </div>
      </div>
    )
  }

  // Show the error page only when there is nothing to render. A failed
  // BACKGROUND refetch (socket invalidation, window refocus) keeps `error` set
  // while `ticket` still holds the last good data — replacing a fully rendered
  // ticket with an error screen was the "can't view a ticket for more than 3
  // seconds" bug; the effect above surfaces those as a toast instead.
  if (!ticket) {
    return (
      <div className="flex flex-col items-center justify-center h-96" role="alert">
        <p className="text-lg font-semibold text-destructive">Error loading ticket</p>
        <p className="text-sm text-muted-foreground mt-2">
          {explainError(error, 'Ticket not found')}
        </p>
        <Button
          onClick={() => navigate('/tickets')}
          className="mt-4"
          aria-label="Return to tickets list"
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Back to Tickets
        </Button>
      </div>
    )
  }

  const formatDate = (date?: string) => {
    if (!date) return 'N/A'
    return (toDisplayDate(date) ?? new Date(date)).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const handleStartEdit = () => {
    if (!ticket) return
    setEditForm({
      subject: ticket.subject || '',
      description: ticket.description || '',
      channel: ticket.channel || 'manual',
      category: ticket.category || '',
      jira: ticket.jira || '',
      account_id: ticket.account_id || '',
      contact_id: ticket.contact_id || '',
      product_id: ticket.product_id || '',
      owner_id: ticket.owner_id || '',
    })
    setFormErrors({})
    setIsEditMode(true)
  }

  const handleCancelEdit = () => {
    setIsEditMode(false)
    setEditForm({})
    setFormErrors({})
  }

  const handleFieldChange = (field: string, value: string) => {
    setEditForm((prev) => ({ ...prev, [field]: value }))
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleSaveEdit = async () => {
    const errors: Record<string, string> = {}
    if (!editForm.subject?.trim()) errors.subject = 'Subject is required'
    if (!editForm.owner_id) errors.owner_id = 'Owner is required'

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      toast({
        title: 'Validation Error',
        description: 'Please fix the highlighted fields',
        variant: 'destructive',
      })
      return
    }

    try {
      await updateMutation.mutateAsync({
        id: ticket!.id,
        data: {
          subject: editForm.subject,
          description: editForm.description,
          channel: (editForm.channel || 'manual') as TicketChannel,
          category: editForm.category,
          jira: editForm.jira,
          account_id: editForm.account_id,
          contact_id: editForm.contact_id,
          product_id: editForm.product_id,
          owner_id: editForm.owner_id,
        } as any,
      })
      setIsEditMode(false)
      setEditForm({})
      setFormErrors({})
      toast({
        title: 'Ticket Updated',
        description: 'Ticket has been updated successfully',
      })
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update ticket',
        variant: 'destructive',
      })
    }
  }

  const handleStatusChange = async (newStatus: TicketStatus) => {
    try {
      await updateMutation.mutateAsync({ id: ticket.id, data: { status: newStatus } })
      toast({
        title: 'Status Updated',
        description: `Ticket status changed to ${newStatus}`,
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      })
    }
  }

  const handlePriorityChange = async (newPriority: TicketPriority) => {
    try {
      await updateMutation.mutateAsync({ id: ticket.id, data: { priority: newPriority } })
      toast({
        title: 'Priority Updated',
        description: `Ticket priority changed to ${newPriority}`,
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update priority',
        variant: 'destructive',
      })
    }
  }

  const handleAddComment = async () => {
    if (!newComment.trim()) return

    // Build comment payload
    const commentPayload: Parameters<typeof addCommentMutation.mutateAsync>[0]['comment'] = {
      body: newComment,
      comment_type: commentType,
      is_public: isPublic,
    }

    // Add call log fields if this is a call log
    if (commentType === 'call_log') {
      commentPayload.direction = callDirection
      const mins = parseInt(callDurationMinutes) || 0
      const secs = parseInt(callDurationSeconds) || 0
      commentPayload.call_duration_seconds = mins * 60 + secs
      if (callPhoneNumber.trim()) {
        commentPayload.call_phone_number = callPhoneNumber.trim()
      }
      commentPayload.call_disposition = callDisposition
      commentPayload.call_start_time = new Date().toISOString()
    }

    try {
      await addCommentMutation.mutateAsync({
        ticketId: ticket.id,
        comment: commentPayload,
      })
      // Reset form
      setNewComment('')
      if (commentType === 'call_log') {
        setCallDurationMinutes('')
        setCallDurationSeconds('')
        setCallPhoneNumber('')
        setTicketCallDisposition('answered')
        setCallDirection('outbound')
      }
      toast({
        title: commentType === 'call_log' ? 'Call Logged' : 'Comment Added',
        description:
          commentType === 'call_log'
            ? 'Call has been logged to the ticket'
            : 'Your comment has been added to the ticket',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to add comment',
        variant: 'destructive',
      })
    }
  }

  // Fallback display for email/webform tickets not linked to a CRM contact/account:
  // surface the submitter's name/email/phone + organization so the panels aren't blank.
  const customFields: Record<string, any> = (() => {
    const v = (ticket as any).custom_fields
    if (!v) return {}
    if (typeof v === 'string') {
      try {
        return JSON.parse(v)
      } catch {
        return {}
      }
    }
    return v
  })()
  const submitterName: string =
    (ticket as any).original_from_name || (ticket as any).original_from_email || ''
  const submitterEmail: string = (ticket as any).original_from_email || ''
  const submitterPhone: string = customFields.phone || ''
  const submitterOrg: string = customFields.organization || ''

  return (
    <div className="space-y-6" role="main" aria-label="Ticket details">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/tickets')}
            aria-label="Back to tickets list"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Badge
            variant="outline"
            style={{
              backgroundColor: CATEGORICAL.navy.tint,
              color: CATEGORICAL.navy.solid,
              borderColor: CATEGORICAL.navy.solid,
            }}
          >
            <Headphones className="mr-1.5 h-3 w-3" aria-hidden="true" />
            GRK-{ticket.ticket_number}
          </Badge>
          <div className="flex-1">
            {isEditMode ? (
              <div className="space-y-1">
                <Input
                  id="edit-subject"
                  value={editForm.subject || ''}
                  onChange={(e) => handleFieldChange('subject', e.target.value)}
                  className="text-2xl font-bold"
                  aria-label="Ticket subject"
                  aria-invalid={!!formErrors.subject}
                  aria-describedby={formErrors.subject ? 'subject-error' : undefined}
                />
                {formErrors.subject && (
                  <p id="subject-error" className="text-sm text-destructive" role="alert">
                    {formErrors.subject}
                  </p>
                )}
              </div>
            ) : (
              (() => {
                const p = bindReadOnly('edit-subject', 'subject')
                return (
                  <h1 {...p} className={`text-3xl font-bold tracking-tight ${p.className}`}>
                    {ticket.subject}
                  </h1>
                )
              })()
            )}
            <p className="text-sm text-muted-foreground mt-1">
              Created {formatDate(ticket.created_date)} by {ticket.created_by_name}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditMode ? (
            <>
              <Button
                variant="outline"
                onClick={handleCancelEdit}
                disabled={updateMutation.isPending}
                aria-label="Cancel editing"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={updateMutation.isPending}
                aria-label="Save ticket changes"
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <>
              <Select
                value={ticket.status}
                onValueChange={(value) => handleStatusChange(value as TicketStatus)}
                disabled={isReadOnly || updateMutation.isPending}
              >
                <SelectTrigger className="w-[130px]" aria-label="Change status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="New">New</SelectItem>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="On Hold">On Hold</SelectItem>
                  <SelectItem value="Escalated">Escalated</SelectItem>
                  <SelectItem value="Resolved">Resolved</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={ticket.priority}
                onValueChange={(value) => handlePriorityChange(value as TicketPriority)}
                disabled={isReadOnly || updateMutation.isPending}
              >
                <SelectTrigger className="w-[110px]" aria-label="Change priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
              {!isReadOnly && (
                <Button variant="outline" onClick={handleStartEdit} aria-label="Edit ticket">
                  <Pencil className="h-4 w-4 mr-2" aria-hidden="true" />
                  Edit
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Status badges */}
      <div className="flex gap-2">
        <Badge variant={statusVariants[ticket.status as TicketStatus]}>{ticket.status}</Badge>
        <Badge variant={priorityVariants[ticket.priority as TicketPriority]}>
          {ticket.priority} Priority
        </Badge>
        <Badge variant="outline">{ticket.channel}</Badge>
        {ticket.category && <Badge variant="secondary">{ticket.category}</Badge>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              {isEditMode ? (
                <Textarea
                  id="edit-description"
                  value={editForm.description || ''}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  rows={6}
                  aria-label="Ticket description"
                />
              ) : (
                (() => {
                  const p = bindReadOnly('edit-description', 'description')
                  return (
                    <p {...p} className={`text-sm whitespace-pre-wrap ${p.className}`}>
                      {ticket.description || 'No description provided.'}
                    </p>
                  )
                })()
              )}
            </CardContent>
          </Card>

          {/* Tabs for Activity Timeline, Tasks, and Emails */}
          <Tabs defaultValue="activity" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="activity" className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" aria-hidden="true" />
                Activity ({ticket.comments?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="tasks" className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                Tasks ({ticketTasks.length})
              </TabsTrigger>
              <TabsTrigger value="emails" className="flex items-center gap-2">
                <Mail className="h-4 w-4" aria-hidden="true" />
                Emails ({ticketEmails.length})
              </TabsTrigger>
              <TabsTrigger value="attachments" className="flex items-center gap-2">
                <Paperclip className="h-4 w-4" aria-hidden="true" />
                Attachments
              </TabsTrigger>
            </TabsList>

            {/* Activity Timeline Tab */}
            <TabsContent value="activity">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" aria-hidden="true" />
                    Activity Timeline
                  </CardTitle>
                  <CardDescription>
                    {ticket.comments?.length || 0} comment{ticket.comments?.length !== 1 ? 's' : ''}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add Comment Form */}
                  {canComment && (
                    <div className="space-y-3 pb-4 border-b">
                      <div className="flex items-center gap-2 mb-2">
                        <Select
                          value={commentType}
                          onValueChange={(value) => {
                            setCommentType(value as CommentType)
                            // Default to not public for internal notes
                            if (value === 'note') setIsPublic(false)
                            else if (value === 'reply') setIsPublic(true)
                          }}
                        >
                          <SelectTrigger className="w-[130px]" aria-label="Comment type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="reply">Reply</SelectItem>
                            <SelectItem value="note">Internal Note</SelectItem>
                            <SelectItem value="call_log">Call Log</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                          </SelectContent>
                        </Select>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={isPublic}
                            onChange={(e) => setIsPublic(e.target.checked)}
                            className="rounded"
                            aria-label="Visible to customer"
                          />
                          Public
                        </label>
                      </div>

                      {/* Call Log Fields */}
                      {commentType === 'call_log' && (
                        <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-lg">
                          <div className="space-y-1">
                            <Label htmlFor="call-direction" className="text-xs">
                              Direction
                            </Label>
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                size="sm"
                                variant={callDirection === 'inbound' ? 'default' : 'outline'}
                                onClick={() => setCallDirection('inbound')}
                                className="flex-1"
                                aria-pressed={callDirection === 'inbound'}
                              >
                                <PhoneIncoming className="h-3 w-3 mr-1" aria-hidden="true" />
                                Inbound
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant={callDirection === 'outbound' ? 'default' : 'outline'}
                                onClick={() => setCallDirection('outbound')}
                                className="flex-1"
                                aria-pressed={callDirection === 'outbound'}
                              >
                                <PhoneOutgoing className="h-3 w-3 mr-1" aria-hidden="true" />
                                Outbound
                              </Button>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="call-duration" className="text-xs">
                              Duration
                            </Label>
                            <div className="flex items-center gap-1">
                              <Input
                                id="call-duration-mins"
                                type="number"
                                min="0"
                                placeholder="0"
                                value={callDurationMinutes}
                                onChange={(e) => setCallDurationMinutes(e.target.value)}
                                className="w-16 text-center"
                                aria-label="Duration minutes"
                              />
                              <span className="text-xs text-muted-foreground">min</span>
                              <Input
                                id="call-duration-secs"
                                type="number"
                                min="0"
                                max="59"
                                placeholder="0"
                                value={callDurationSeconds}
                                onChange={(e) => setCallDurationSeconds(e.target.value)}
                                className="w-16 text-center"
                                aria-label="Duration seconds"
                              />
                              <span className="text-xs text-muted-foreground">sec</span>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="call-phone" className="text-xs">
                              Phone Number
                            </Label>
                            <Input
                              id="call-phone"
                              type="tel"
                              placeholder={ticket.contact_phone || 'Phone number'}
                              value={callPhoneNumber}
                              onChange={(e) => setCallPhoneNumber(e.target.value)}
                              aria-label="Call phone number"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="call-disposition" className="text-xs">
                              Disposition
                            </Label>
                            <Select
                              value={callDisposition}
                              onValueChange={(value) =>
                                setTicketCallDisposition(value as TicketCallDisposition)
                              }
                            >
                              <SelectTrigger id="call-disposition" aria-label="Call disposition">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="answered">Answered</SelectItem>
                                <SelectItem value="voicemail">Voicemail</SelectItem>
                                <SelectItem value="missed">Missed</SelectItem>
                                <SelectItem value="busy">Busy</SelectItem>
                                <SelectItem value="no_answer">No Answer</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}

                      <Textarea
                        placeholder={
                          commentType === 'call_log' ? 'Call notes...' : 'Add a comment...'
                        }
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        rows={3}
                        aria-label={commentType === 'call_log' ? 'Call notes' : 'New comment'}
                      />
                      <div className="flex justify-end">
                        <Button
                          onClick={handleAddComment}
                          disabled={!newComment.trim() || addCommentMutation.isPending}
                        >
                          <Send className="mr-2 h-4 w-4" aria-hidden="true" />
                          {addCommentMutation.isPending
                            ? 'Sending...'
                            : commentType === 'call_log'
                              ? 'Log Call'
                              : 'Add Comment'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Comments List */}
                  {ticket.comments && ticket.comments.length > 0 ? (
                    <div className="space-y-4">
                      {ticket.comments.map((comment: TicketComment) => {
                        const canModify =
                          !impersonationReadOnly &&
                          !!currentUser?.id &&
                          (comment.author_id === currentUser.id || isAdmin)
                        const isEditing = editingCommentId === comment.id

                        return (
                          <div
                            key={comment.id}
                            className={`p-4 rounded-lg`}
                            style={
                              comment.is_public
                                ? undefined
                                : {
                                    backgroundColor: CATEGORICAL.pollen.tint,
                                    borderWidth: '1px',
                                    borderStyle: 'solid',
                                    borderColor: CATEGORICAL.clay.solid,
                                  }
                            }
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                {/* Call log direction icon */}
                                {comment.comment_type === 'call_log' &&
                                  comment.direction &&
                                  (comment.direction === 'inbound' ? (
                                    <PhoneIncoming
                                      className="h-4 w-4"
                                      style={{ color: CATEGORICAL.evergreen.solid }}
                                      aria-label="Inbound call"
                                    />
                                  ) : (
                                    <PhoneOutgoing
                                      className="h-4 w-4"
                                      style={{ color: CATEGORICAL.teal.solid }}
                                      aria-label="Outbound call"
                                    />
                                  ))}
                                <span className="font-medium text-sm">{comment.author_name}</span>
                                <Badge variant="outline" className="text-xs">
                                  {commentTypeLabels[comment.comment_type as CommentType]}
                                </Badge>
                                {!comment.is_public && (
                                  <Badge variant="secondary" className="text-xs">
                                    Internal
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">
                                  {formatDate(comment.created_date)}
                                  {comment.updated_date && ' (edited)'}
                                </span>
                                {canModify && !isEditing && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        aria-label="Comment actions"
                                      >
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem
                                        onClick={() => {
                                          setEditingCommentId(comment.id)
                                          setEditingCommentBody(comment.body)
                                        }}
                                      >
                                        <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
                                        Edit
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        className="text-destructive"
                                        onClick={() => setDeletingCommentId(comment.id)}
                                      >
                                        <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                                        Delete
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </div>
                            </div>

                            {/* Call log details */}
                            {comment.comment_type === 'call_log' && (
                              <div className="flex flex-wrap gap-2 mb-2">
                                {comment.call_duration_seconds !== undefined &&
                                  comment.call_duration_seconds > 0 && (
                                    <Badge variant="outline" className="text-xs">
                                      <Clock className="h-3 w-3 mr-1" aria-hidden="true" />
                                      {formatDuration(comment.call_duration_seconds)}
                                    </Badge>
                                  )}
                                {comment.call_phone_number && (
                                  <Badge variant="outline" className="text-xs">
                                    <Phone className="h-3 w-3 mr-1" aria-hidden="true" />
                                    {comment.call_phone_number}
                                  </Badge>
                                )}
                                {comment.call_disposition && (
                                  <Badge
                                    variant={
                                      comment.call_disposition === 'answered'
                                        ? 'default'
                                        : 'secondary'
                                    }
                                    className="text-xs"
                                  >
                                    {callDispositionLabels[comment.call_disposition]}
                                  </Badge>
                                )}
                              </div>
                            )}

                            {isEditing ? (
                              <div className="space-y-2">
                                <Textarea
                                  value={editingCommentBody}
                                  onChange={(e) => setEditingCommentBody(e.target.value)}
                                  className="min-h-[80px] text-sm"
                                  aria-label="Edit comment"
                                />
                                <div className="flex gap-2 justify-end">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setEditingCommentId(null)}
                                    disabled={updateCommentMutation.isPending}
                                  >
                                    <X className="mr-1 h-3 w-3" aria-hidden="true" />
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    disabled={
                                      !editingCommentBody.trim() || updateCommentMutation.isPending
                                    }
                                    onClick={async () => {
                                      try {
                                        await updateCommentMutation.mutateAsync({
                                          ticketId: id!,
                                          commentId: comment.id,
                                          updates: { body: editingCommentBody.trim() },
                                        })
                                        setEditingCommentId(null)
                                        toast({ title: 'Comment updated' })
                                      } catch (err: any) {
                                        toast({
                                          title: 'Error',
                                          description:
                                            err?.response?.data?.error ||
                                            'Failed to update comment',
                                          variant: 'destructive',
                                        })
                                      }
                                    }}
                                  >
                                    <Check className="mr-1 h-3 w-3" aria-hidden="true" />
                                    {updateCommentMutation.isPending ? 'Saving...' : 'Save'}
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm whitespace-pre-wrap">{comment.body}</p>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No comments yet. Add a comment to start the conversation.
                    </p>
                  )}

                  {/* Delete comment confirmation dialog */}
                  <AlertDialog
                    open={!!deletingCommentId}
                    onOpenChange={(open) => !open && setDeletingCommentId(null)}
                  >
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Comment</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this comment? This action cannot be
                          undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={async () => {
                            if (!deletingCommentId) return
                            try {
                              await deleteCommentMutation.mutateAsync({
                                ticketId: id!,
                                commentId: deletingCommentId,
                              })
                              setDeletingCommentId(null)
                              toast({ title: 'Comment deleted' })
                            } catch (err: any) {
                              toast({
                                title: 'Error',
                                description: explainError(err, 'Failed to delete comment'),
                                variant: 'destructive',
                              })
                            }
                          }}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tasks Tab */}
            <TabsContent value="tasks">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
                        Tasks
                      </CardTitle>
                      <CardDescription>Tasks and reminders linked to this ticket</CardDescription>
                    </div>
                    {!isReadOnly && (
                      <Button
                        size="sm"
                        onClick={() => setShowTaskForm(!showTaskForm)}
                        aria-label={
                          showTaskForm ? 'Cancel new task' : 'Create new task for this ticket'
                        }
                      >
                        {showTaskForm ? (
                          <>
                            <X className="h-4 w-4 mr-1" aria-hidden="true" />
                            Cancel
                          </>
                        ) : (
                          <>
                            <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
                            New Task
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Inline task creation form */}
                  {showTaskForm && (
                    <form
                      className="border rounded-lg p-4 mb-4 space-y-3 bg-muted/30"
                      onSubmit={async (e) => {
                        e.preventDefault()
                        if (!taskForm.subject.trim() || !taskForm.activity_date) return
                        try {
                          await createTicketTaskMutation.mutateAsync({
                            ticketId: id!,
                            data: {
                              subject: taskForm.subject.trim(),
                              activity_date: taskForm.activity_date,
                              priority: taskForm.priority,
                              description: taskForm.description || undefined,
                              owner_id: currentUser?.id,
                            },
                          })
                          setTaskForm({
                            subject: '',
                            activity_date: '',
                            priority: 'Normal',
                            description: '',
                          })
                          setShowTaskForm(false)
                          toast({
                            title: 'Task created',
                            description: 'Task has been linked to this ticket.',
                          })
                        } catch {
                          toast({
                            title: 'Error',
                            description: 'Failed to create task.',
                            variant: 'destructive',
                          })
                        }
                      }}
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="task-subject">Subject *</Label>
                          <Input
                            id="task-subject"
                            value={taskForm.subject}
                            onChange={(e) =>
                              setTaskForm((prev) => ({ ...prev, subject: e.target.value }))
                            }
                            placeholder="e.g., Follow up with customer"
                            aria-required="true"
                          />
                        </div>
                        <div>
                          <Label htmlFor="task-due-date">Due Date *</Label>
                          <DatePicker
                            id="task-due-date"
                            value={taskForm.activity_date}
                            onChange={(v) =>
                              setTaskForm((prev) => ({ ...prev, activity_date: v ?? '' }))
                            }
                            aria-required="true"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="task-priority">Priority</Label>
                          <select
                            id="task-priority"
                            value={taskForm.priority}
                            onChange={(e) =>
                              setTaskForm((prev) => ({ ...prev, priority: e.target.value }))
                            }
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <option value="Low">Low</option>
                            <option value="Normal">Normal</option>
                            <option value="High">High</option>
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="task-description">Description</Label>
                          <Input
                            id="task-description"
                            value={taskForm.description}
                            onChange={(e) =>
                              setTaskForm((prev) => ({ ...prev, description: e.target.value }))
                            }
                            placeholder="Optional description"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Button
                          type="submit"
                          size="sm"
                          disabled={
                            createTicketTaskMutation.isPending ||
                            !taskForm.subject.trim() ||
                            !taskForm.activity_date
                          }
                        >
                          {createTicketTaskMutation.isPending ? 'Creating...' : 'Create Task'}
                        </Button>
                      </div>
                    </form>
                  )}

                  {ticketTasks.length === 0 && !showTaskForm ? (
                    <p className="text-center py-8 text-muted-foreground">
                      No tasks yet. Create a task to track actions for this ticket.
                    </p>
                  ) : ticketTasks.length > 0 ? (
                    <div className="space-y-3" role="list" aria-label="Ticket tasks">
                      {ticketTasks.map((task) => (
                        <div
                          key={task.id}
                          role="listitem"
                          className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {task.status === 'Completed' ? (
                              <CheckCircle2
                                className="h-5 w-5 shrink-0"
                                style={{ color: CATEGORICAL.evergreen.solid }}
                                aria-hidden="true"
                              />
                            ) : (
                              <Circle
                                className="h-5 w-5 text-muted-foreground shrink-0"
                                aria-hidden="true"
                              />
                            )}
                            <div className="min-w-0">
                              <button
                                onClick={() => navigate(`/tasks/${task.id}`)}
                                className="text-sm font-medium text-primary hover:underline truncate block text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                                aria-label={`View task: ${task.subject}`}
                              >
                                {task.subject}
                              </button>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                {task.activity_date && (
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" aria-hidden="true" />
                                    {formatDateOnly(task.activity_date)}
                                  </span>
                                )}
                                {task.owner_name && <span>{task.owner_name}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge
                              variant={
                                task.status === 'Completed'
                                  ? 'success'
                                  : task.status === 'In Progress'
                                    ? 'warning'
                                    : 'outline'
                              }
                            >
                              {task.status}
                            </Badge>
                            {task.priority && task.priority !== 'Normal' && (
                              <Badge
                                variant={task.priority === 'High' ? 'destructive' : 'secondary'}
                              >
                                {task.priority}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Emails Tab */}
            <TabsContent value="emails">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5" aria-hidden="true" />
                        Emails
                      </CardTitle>
                      <CardDescription>
                        {ticketEmails.length} email{ticketEmails.length !== 1 ? 's' : ''} linked to
                        this ticket
                      </CardDescription>
                    </div>
                    {!isReadOnly && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setShowLinkModal(true)}>
                          <Link2 className="h-4 w-4 mr-1" aria-hidden="true" />
                          Link Email
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setEmailTo(ticket.contact_email || '')
                            setEmailSubject(`[GRK-${ticket.ticket_number}] `)
                            setEmailBody('')
                            setShowComposeModal(true)
                          }}
                        >
                          <Send className="h-4 w-4 mr-1" aria-hidden="true" />
                          Send Email
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {ticketEmails.length > 0 ? (
                    <div className="space-y-3">
                      {ticketEmails.map((email: Email) => (
                        <div
                          key={email.id}
                          className="p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                {email.incoming ? (
                                  <Badge variant="outline" className="text-xs">
                                    <Inbox className="h-3 w-3 mr-1" aria-hidden="true" />
                                    Received
                                  </Badge>
                                ) : (
                                  <Badge variant="default" className="text-xs">
                                    <Send className="h-3 w-3 mr-1" aria-hidden="true" />
                                    Sent
                                  </Badge>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {formatDate(email.message_date)}
                                </span>
                              </div>
                              <p className="font-medium text-sm truncate">{email.subject}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {email.incoming
                                  ? `From: ${email.from_address}`
                                  : `To: ${email.to_address}`}
                              </p>
                            </div>
                            {!isReadOnly && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={async () => {
                                  try {
                                    await unlinkEmailMutation.mutateAsync({
                                      ticketId: ticket.id,
                                      emailId: email.id,
                                    })
                                    refetchEmails()
                                    toast({
                                      title: 'Email Unlinked',
                                      description: 'Email has been unlinked from this ticket',
                                    })
                                  } catch (error) {
                                    toast({
                                      title: 'Error',
                                      description: 'Failed to unlink email',
                                      variant: 'destructive',
                                    })
                                  }
                                }}
                                disabled={unlinkEmailMutation.isPending}
                                aria-label="Unlink email"
                              >
                                <Unlink className="h-4 w-4" aria-hidden="true" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Mail
                        className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3"
                        aria-hidden="true"
                      />
                      <p className="text-sm text-muted-foreground">
                        No emails linked to this ticket yet.
                      </p>
                      {!isReadOnly && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Click "Link Email" to attach an existing email or "Send Email" to compose
                          a new one.
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="attachments">
              <AttachmentsTab entityId={id!} entityType="Ticket" />
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar - Account & Contact Info */}
        <div className="space-y-6">
          {/* Account Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" aria-hidden="true" />
                Account
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditMode ? (
                <RecordCombobox
                  id="edit-account"
                  records={accounts}
                  value={editForm.account_id || ''}
                  onValueChange={(value) => {
                    handleFieldChange('account_id', value)
                    handleFieldChange('contact_id', '')
                    setContactSearch('')
                  }}
                  placeholder="Select account"
                  searchPlaceholder="Search accounts..."
                  emptyMessage="No accounts found."
                  displayField={(acc: any) => acc.name || acc.account_name || ''}
                  aria-label="Account"
                  onSearchChange={setAccountSearch}
                  isLoading={accountsLoading}
                  selectedLabel={ticket.account_name || undefined}
                />
              ) : ticket.account_name ? (
                (() => {
                  const p = bindReadOnly('edit-account', 'account')
                  return (
                    <div {...p} className={p.className}>
                      <div className="py-2 border-b border-border">
                        <Label className="text-xs text-muted-foreground">Name</Label>
                        <p className="text-sm font-medium">{ticket.account_name}</p>
                      </div>
                      {ticket.account_phone && (
                        <div
                          className="py-2 border-b border-border"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Label className="text-xs text-muted-foreground">Phone</Label>
                          <p className="text-sm flex items-center gap-1">
                            <Phone className="h-3 w-3" aria-hidden="true" />
                            <ClickToCallButton
                              phoneNumber={ticket.account_phone}
                              whatId={ticket.account_id}
                              whatType="Account"
                            />
                          </p>
                        </div>
                      )}
                      {ticket.account_website && (
                        <div className="py-2 border-b border-border">
                          <Label className="text-xs text-muted-foreground">Website</Label>
                          <p className="text-sm flex items-center gap-1">
                            <Globe className="h-3 w-3" aria-hidden="true" />
                            <a
                              href={ticket.account_website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {ticket.account_website}
                            </a>
                          </p>
                        </div>
                      )}
                    </div>
                  )
                })()
              ) : submitterOrg ? (
                (() => {
                  const p = bindReadOnly('edit-account', 'account')
                  return (
                    <div {...p} className={p.className}>
                      <div className="py-2 border-b border-border">
                        <Label className="text-xs text-muted-foreground">Organization</Label>
                        <p className="text-sm font-medium">{submitterOrg}</p>
                      </div>
                      <p className="pt-2 text-xs text-muted-foreground">
                        Submitted via web form — not yet linked to a CRM account.
                      </p>
                    </div>
                  )
                })()
              ) : (
                (() => {
                  const p = bindReadOnly('edit-account', 'account')
                  return (
                    <p {...p} className={`text-sm text-muted-foreground ${p.className}`}>
                      No account linked
                    </p>
                  )
                })()
              )}
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" aria-hidden="true" />
                Contact
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditMode ? (
                <RecordCombobox
                  id="edit-contact"
                  records={allContacts}
                  value={editForm.contact_id || ''}
                  onValueChange={(value) => handleFieldChange('contact_id', value)}
                  placeholder="Select contact"
                  searchPlaceholder="Search contacts..."
                  emptyMessage="No contacts found."
                  displayField={(c: any) => `${c.first_name || ''} ${c.last_name || ''}`.trim()}
                  aria-label="Contact"
                  onSearchChange={setContactSearch}
                  isLoading={contactsLoading}
                  selectedLabel={ticket.contact_name || undefined}
                />
              ) : ticket.contact_name?.trim() ? (
                (() => {
                  const p = bindReadOnly('edit-contact', 'contact')
                  return (
                    <div {...p} className={p.className}>
                      <div className="py-2 border-b border-border">
                        <Label className="text-xs text-muted-foreground">Name</Label>
                        <p className="text-sm font-medium">{ticket.contact_name}</p>
                      </div>
                      {ticket.contact_email && (
                        <div className="py-2 border-b border-border">
                          <Label className="text-xs text-muted-foreground">Email</Label>
                          <p className="text-sm flex items-center gap-1">
                            <Mail className="h-3 w-3" aria-hidden="true" />
                            <a
                              href={`mailto:${ticket.contact_email}`}
                              className="text-primary hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {ticket.contact_email}
                            </a>
                          </p>
                        </div>
                      )}
                      {ticket.contact_phone && (
                        <div
                          className="py-2 border-b border-border"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Label className="text-xs text-muted-foreground">Phone</Label>
                          <p className="text-sm flex items-center gap-1">
                            <Phone className="h-3 w-3" aria-hidden="true" />
                            <ClickToCallButton
                              phoneNumber={ticket.contact_phone}
                              whoId={ticket.contact_id}
                              whoType="Contact"
                              whoName={ticket.contact_name}
                              whatId={ticket.account_id}
                              whatType="Account"
                            />
                          </p>
                        </div>
                      )}
                    </div>
                  )
                })()
              ) : submitterName || submitterEmail ? (
                (() => {
                  const p = bindReadOnly('edit-contact', 'contact')
                  return (
                    <div {...p} className={p.className}>
                      <div className="py-2 border-b border-border">
                        <Label className="text-xs text-muted-foreground">Name</Label>
                        <p className="text-sm font-medium">{submitterName || '—'}</p>
                      </div>
                      {submitterEmail && (
                        <div className="py-2 border-b border-border">
                          <Label className="text-xs text-muted-foreground">Email</Label>
                          <p className="text-sm flex items-center gap-1">
                            <Mail className="h-3 w-3" aria-hidden="true" />
                            <a
                              href={`mailto:${submitterEmail}`}
                              className="text-primary hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {submitterEmail}
                            </a>
                          </p>
                        </div>
                      )}
                      {submitterPhone && (
                        <div
                          className="py-2 border-b border-border"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Label className="text-xs text-muted-foreground">Phone</Label>
                          <p className="text-sm flex items-center gap-1">
                            <Phone className="h-3 w-3" aria-hidden="true" />
                            {submitterPhone}
                          </p>
                        </div>
                      )}
                      <p className="pt-2 text-xs text-muted-foreground">
                        Submitted via web form — not yet linked to a CRM contact.
                      </p>
                    </div>
                  )
                })()
              ) : (
                (() => {
                  const p = bindReadOnly('edit-contact', 'contact')
                  return (
                    <p {...p} className={`text-sm text-muted-foreground ${p.className}`}>
                      No contact linked
                    </p>
                  )
                })()
              )}
            </CardContent>
          </Card>

          {/* Ticket Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" aria-hidden="true" />
                Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="py-2 border-b border-border">
                <Label htmlFor="edit-owner" className="text-xs text-muted-foreground">
                  Owner
                </Label>
                {isEditMode ? (
                  <div className="mt-1">
                    <Select
                      value={editForm.owner_id || ''}
                      onValueChange={(value) => handleFieldChange('owner_id', value)}
                    >
                      <SelectTrigger
                        id="edit-owner"
                        aria-label="Select owner"
                        aria-invalid={!!formErrors.owner_id}
                        aria-describedby={formErrors.owner_id ? 'owner-error' : undefined}
                      >
                        <SelectValue placeholder="Select owner" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((u: any) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formErrors.owner_id && (
                      <p id="owner-error" className="text-sm text-destructive mt-1" role="alert">
                        {formErrors.owner_id}
                      </p>
                    )}
                  </div>
                ) : (
                  (() => {
                    const p = bindReadOnly('edit-owner', 'owner')
                    return (
                      <p {...p} className={`text-sm ${p.className}`}>
                        {ticket.owner_name || 'Unassigned'}
                      </p>
                    )
                  })()
                )}
              </div>
              <div className="py-2 border-b border-border">
                <Label htmlFor="edit-category" className="text-xs text-muted-foreground">
                  Category
                </Label>
                {isEditMode ? (
                  <Input
                    id="edit-category"
                    value={editForm.category || ''}
                    onChange={(e) => handleFieldChange('category', e.target.value)}
                    placeholder="Enter category"
                    className="mt-1"
                    aria-label="Ticket category"
                  />
                ) : (
                  (() => {
                    const p = bindReadOnly('edit-category', 'category')
                    return (
                      <p {...p} className={`text-sm ${p.className}`}>
                        {ticket.category || 'None'}
                      </p>
                    )
                  })()
                )}
              </div>
              <div className="py-2 border-b border-border">
                <Label htmlFor="edit-jira" className="text-xs text-muted-foreground">
                  Jira
                </Label>
                {isEditMode ? (
                  <Input
                    id="edit-jira"
                    value={editForm.jira || ''}
                    onChange={(e) => handleFieldChange('jira', e.target.value)}
                    placeholder="e.g. GRACKLE-123"
                    className="mt-1"
                    aria-label="Jira issue key"
                  />
                ) : (
                  (() => {
                    const p = bindReadOnly('edit-jira', 'Jira')
                    return (
                      <p {...p} className={`text-sm ${p.className}`}>
                        {ticket.jira || 'None'}
                      </p>
                    )
                  })()
                )}
              </div>
              <div className="py-2 border-b border-border">
                <Label htmlFor="edit-channel" className="text-xs text-muted-foreground">
                  Channel
                </Label>
                {isEditMode ? (
                  <Select
                    value={editForm.channel || 'manual'}
                    onValueChange={(value) => handleFieldChange('channel', value)}
                  >
                    <SelectTrigger id="edit-channel" className="mt-1" aria-label="Select channel">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="chat">Chat</SelectItem>
                      <SelectItem value="web">Web</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  (() => {
                    const p = bindReadOnly('edit-channel', 'channel')
                    return (
                      <p {...p} className={`text-sm capitalize ${p.className}`}>
                        {ticket.channel || 'Manual'}
                      </p>
                    )
                  })()
                )}
              </div>
              <div className="py-2 border-b border-border">
                <Label htmlFor="edit-product" className="text-xs text-muted-foreground">
                  Product
                </Label>
                {isEditMode ? (
                  <RecordCombobox
                    id="edit-product"
                    records={products}
                    value={editForm.product_id || ''}
                    onValueChange={(value) => handleFieldChange('product_id', value)}
                    placeholder="Select product"
                    searchPlaceholder="Search products..."
                    emptyMessage="No products found."
                    displayField={(p) => p.name || ''}
                    aria-label="Product"
                    onSearchChange={setProductSearch}
                    isLoading={productsLoading}
                    selectedLabel={ticket.product_name || undefined}
                  />
                ) : (
                  (() => {
                    const p = bindReadOnly('edit-product', 'product')
                    return (
                      <p {...p} className={`text-sm ${p.className}`}>
                        {ticket.product_name || 'None'}
                      </p>
                    )
                  })()
                )}
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-xs text-muted-foreground">Created</Label>
                <p className="text-sm">{formatDate(ticket.created_date)}</p>
              </div>
              {ticket.first_response_at && (
                <div className="py-2 border-b border-border">
                  <Label className="text-xs text-muted-foreground">First Response</Label>
                  <p className="text-sm">{formatDate(ticket.first_response_at)}</p>
                </div>
              )}
              {ticket.resolved_at && (
                <div className="py-2 border-b border-border">
                  <Label className="text-xs text-muted-foreground">Resolved</Label>
                  <p className="text-sm">{formatDate(ticket.resolved_at)}</p>
                </div>
              )}
              {ticket.closed_at && (
                <div className="py-2 border-b border-border">
                  <Label className="text-xs text-muted-foreground">Closed</Label>
                  <p className="text-sm">{formatDate(ticket.closed_at)}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* SLA Metrics */}
          {ticket.sla_status && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Timer className="h-5 w-5" aria-hidden="true" />
                  SLA Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SLAMetrics
                  responseSlaHours={ticket.response_sla_hours}
                  resolutionSlaHours={ticket.resolution_sla_hours}
                  responseStatus={ticket.sla_status.response_status}
                  resolutionStatus={ticket.sla_status.resolution_status}
                  responseTimeRemaining={ticket.sla_status.response_time_remaining_hours}
                  resolutionTimeRemaining={ticket.sla_status.resolution_time_remaining_hours}
                  responsePercentUsed={ticket.sla_status.response_percent_used}
                  resolutionPercentUsed={ticket.sla_status.resolution_percent_used}
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Compose Email Modal */}
      <Dialog open={showComposeModal} onOpenChange={setShowComposeModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Send Email</DialogTitle>
            <DialogDescription>
              Compose and send an email for GRK-{ticket.ticket_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email-to">To</Label>
              <Input
                id="email-to"
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="recipient@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-subject">Subject</Label>
              <Input
                id="email-subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Email subject"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-body">Message</Label>
              <Textarea
                id="email-body"
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Type your message..."
                rows={8}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComposeModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!emailTo.trim() || !emailSubject.trim() || !emailBody.trim()) {
                  toast({
                    title: 'Validation Error',
                    description: 'Please fill in all fields',
                    variant: 'destructive',
                  })
                  return
                }
                try {
                  await sendEmailMutation.mutateAsync({
                    ticketId: ticket.id,
                    email: {
                      to: emailTo,
                      subject: emailSubject,
                      body: emailBody,
                    },
                  })
                  setShowComposeModal(false)
                  setEmailTo('')
                  setEmailSubject('')
                  setEmailBody('')
                  refetchEmails()
                  toast({
                    title: 'Email Sent',
                    description: 'Your email has been sent successfully',
                  })
                } catch (error) {
                  toast({
                    title: 'Error',
                    description: explainError(error, 'Failed to send email'),
                    variant: 'destructive',
                  })
                }
              }}
              disabled={sendEmailMutation.isPending}
            >
              <Send className="h-4 w-4 mr-2" aria-hidden="true" />
              {sendEmailMutation.isPending ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Email Modal */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Link Email</DialogTitle>
            <DialogDescription>Search and link an existing email to this ticket</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Input
              placeholder="Search emails by subject or sender..."
              value={emailSearch}
              onChange={(e) => setEmailSearch(e.target.value)}
              aria-label="Search emails"
            />
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {allEmailsData?.data
                ?.filter(
                  (email: Email) =>
                    !ticketEmails.some((te: Email) => te.id === email.id) &&
                    (email.subject?.toLowerCase().includes(emailSearch.toLowerCase()) ||
                      email.from_address?.toLowerCase().includes(emailSearch.toLowerCase()) ||
                      email.to_address?.toLowerCase().includes(emailSearch.toLowerCase()))
                )
                .slice(0, 20)
                .map((email: Email) => (
                  <div
                    key={email.id}
                    className="p-3 rounded-lg border hover:bg-accent/50 cursor-pointer transition-colors"
                    onClick={async () => {
                      try {
                        await linkEmailMutation.mutateAsync({
                          ticketId: ticket.id,
                          emailId: email.id,
                        })
                        setShowLinkModal(false)
                        setEmailSearch('')
                        refetchEmails()
                        toast({
                          title: 'Email Linked',
                          description: 'Email has been linked to this ticket',
                        })
                      } catch (error) {
                        toast({
                          title: 'Error',
                          description: 'Failed to link email',
                          variant: 'destructive',
                        })
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.currentTarget.click()
                      }
                    }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {email.incoming ? (
                        <Badge variant="outline" className="text-xs">
                          Received
                        </Badge>
                      ) : (
                        <Badge variant="default" className="text-xs">
                          Sent
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatDate(email.message_date)}
                      </span>
                    </div>
                    <p className="font-medium text-sm truncate">{email.subject}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {email.incoming ? `From: ${email.from_address}` : `To: ${email.to_address}`}
                    </p>
                  </div>
                ))}
              {allEmailsData?.data?.filter(
                (email: Email) =>
                  !ticketEmails.some((te: Email) => te.id === email.id) &&
                  (email.subject?.toLowerCase().includes(emailSearch.toLowerCase()) ||
                    email.from_address?.toLowerCase().includes(emailSearch.toLowerCase()) ||
                    email.to_address?.toLowerCase().includes(emailSearch.toLowerCase()))
              ).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No emails found. Try a different search term.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowLinkModal(false)
                setEmailSearch('')
              }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
